// import React, { useEffect, useContext} from 'react';
// import { Alert, Button } from "antd";
// import { Field, ErrorMessage } from "formik";

import React, { useContext, useEffect, useState } from "react";
import { Button, Checkbox, Radio, Select, Input } from "antd";
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { ProductContext } from "context/ProductContext"; // Assumed context for product data
import axios from "axios"; // For API calls

const { Option } = Select;

const Additional = ({ nextStep }) => {
  const { productData, setProductData, next } = useContext(ProductContext);
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(false);

  // Validation schema using Yup
  const validationSchema = Yup.object().shape({
    embedable: Yup.string().required("Embedable selection is required"),
    auto_limit: Yup.number()
      .required("Auto Limit is required")
      .positive("Must be a positive number"),
    premium_notification_frequency: Yup.string().required("Premium Notification Period is required"),
    business_type: Yup.string().required("Business Type is required"),
    ipf_consideration: Yup.string().required("IPF Consideration is required"),
    ipf_entity: Yup.string().required("IPF Entity is required"),
    client_trxn_charges: Yup.string().required("Nexus Charges is required"),
    charges: Yup.array().min(1, "At least one charge must be selected"),
    kyc_checks: Yup.array().min(1, "At least one KYC check must be selected"),
  });

  // Fetch charges data on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get("/v1/api/product_charge/list");
        setCharges(response.data.result.charges || []);
      } catch (error) {
        console.error("Error fetching charges:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLoading(true);
      const updatedProductData = {
        ...productData,
        ...values,
      };
      setProductData(updatedProductData);

      // API call to add additional product details
      await axios.post("/v1/api/product/additional_details", {
        update_details: values,
        product_code: productData.product_code,
      });

      nextStep(); // Proceed to the next step
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Failed to save additional info. Please try again.");
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="additional-info">
      <h2 className="text-2xl font-bold mb-6">Additional Info</h2>
      <Formik
        initialValues={{
          embedable: productData.embedable || "true",
          auto_limit: productData.auto_limit || "",
          premium_notification_frequency: productData.premium_notification_frequency || "",
          business_type: productData.business_type || "DIRECT",
          ipf_consideration: productData.ipf_consideration || "YES",
          ipf_entity: productData.ipf_entity || "US",
          client_trxn_charges: productData.client_trxn_charges || "ON-US",
          charges: productData.charges || [],
          kyc_checks: productData.kyc_checks || [],
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, errors, touched }) => (
          <FormikForm>
            <div className="row">
              {/* Embedable */}
              <div className="col-md-4 mb-3">
                <div className="border p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Product Embedable?</h3>
                  <Field name="embedable">
                    {({ field }) => (
                      <Radio.Group
                        {...field}
                        onChange={(e) => setFieldValue("embedable", e.target.value)}
                        value={values.embedable}
                      >
                        <Radio value="true">YES</Radio>
                        <Radio value="false">NO</Radio>
                      </Radio.Group>
                    )}
                  </Field>
                  <ErrorMessage name="embedable" component="div" className="text-red-500 text-sm" />
                </div>
              </div>

              {/* Auto Limit */}
              <div className="col-md-4 mb-3">
                <div className="ant-form-item-label">
                  <label>Auto Limit</label>
                </div>
                <Field name="auto_limit">
                  {({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      placeholder="Auto Limit"
                      className="w-full"
                    />
                  )}
                </Field>
                <ErrorMessage name="auto_limit" component="div" className="text-red-500 text-sm" />
              </div>

              {/* Premium Notification Period */}
              <div className="col-md-4 mb-3">
                <div className="ant-form-item-label">
                  <label>Premium Notification Period</label>
                </div>
                <Field name="premium_notification_frequency">
                  {({ field }) => (
                    <Select
                      {...field}
                      onChange={(value) => setFieldValue("premium_notification_frequency", value)}
                      placeholder="Select Premium Notification"
                      className="w-full"
                    >
                      <Option value="THREE_DAYS">3 DAYS</Option>
                      <Option value="ONE_WEEK">One Week</Option>
                      <Option value="TWO_WEEKS">Two Weeks</Option>
                    </Select>
                  )}
                </Field>
                <ErrorMessage
                  name="premium_notification_frequency"
                  component="div"
                  className="text-red-500 text-sm"
                />
              </div>

              {/* Business Type */}
              <div className="col-md-4 mb-3">
                <div className="border p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Business Type</h3>
                  <Field name="business_type">
                    {({ field }) => (
                      <Radio.Group
                        {...field}
                        onChange={(e) => setFieldValue("business_type", e.target.value)}
                        value={values.business_type}
                      >
                        <Radio value="DIRECT">DIRECT</Radio>
                        <Radio value="INTERMEDIARY">INTERMEDIARY</Radio>
                        <Radio value="ALL">ALL</Radio>
                      </Radio.Group>
                    )}
                  </Field>
                  <ErrorMessage name="business_type" component="div" className="text-red-500 text-sm" />
                </div>
              </div>

              {/* IPF Consideration */}
              <div className="col-md-4 mb-3">
                <div className="border p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">IPF Consideration</h3>
                  <Field name="ipf_consideration">
                    {({ field }) => (
                      <Radio.Group
                        {...field}
                        onChange={(e) => setFieldValue("ipf_consideration", e.target.value)}
                        value={values.ipf_consideration}
                      >
                        <Radio value="YES">YES</Radio>
                        <Radio value="NO">NO</Radio>
                      </Radio.Group>
                    )}
                  </Field>
                  <ErrorMessage name="ipf_consideration" component="div" className="text-red-500 text-sm" />
                </div>
              </div>

              {/* IPF Entity */}
              <div className="col-md-4 mb-3">
                <div className="border p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">IPF Entity</h3>
                  <Field name="ipf_entity">
                    {({ field }) => (
                      <Radio.Group
                        {...field}
                        onChange={(e) => setFieldValue("ipf_entity", e.target.value)}
                        value={values.ipf_entity}
                      >
                        <Radio value="US">SELF</Radio>
                        <Radio value="OTHERS">OTHERS</Radio>
                      </Radio.Group>
                    )}
                  </Field>
                  <ErrorMessage name="ipf_entity" component="div" className="text-red-500 text-sm" />
                </div>
              </div>

              {/* Charges */}
              <div className="col-md-4 mb-3">
                <div className="border p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Charges</h3>
                  <Field name="charges">
                    {({ field }) => (
                      <Checkbox.Group
                        {...field}
                        onChange={(checkedValues) => setFieldValue("charges", checkedValues)}
                        value={values.charges}
                      >
                        {charges.map((charge) => (
                          <Checkbox key={charge.charge_code} value={charge.charge_code}>
                            {charge.charge_name}
                          </Checkbox>
                        ))}
                      </Checkbox.Group>
                    )}
                  </Field>
                  <ErrorMessage name="charges" component="div" className="text-red-500 text-sm" />
                </div>
              </div>

              {/* Nexus Charges */}
              <div className="col-md-4 mb-3">
                <div className="border p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Nexus Charges</h3>
                  <Field name="client_trxn_charges">
                    {({ field }) => (
                      <Radio.Group
                        {...field}
                        onChange={(e) => setFieldValue("client_trxn_charges", e.target.value)}
                        value={values.client_trxn_charges}
                      >
                        <Radio value="ON-US">On-US</Radio>
                        <Radio value="ON-CLIENT">On-CLIENT</Radio>
                      </Radio.Group>
                    )}
                  </Field>
                  <ErrorMessage name="client_trxn_charges" component="div" className="text-red-500 text-sm" />
                </div>
              </div>

              {/* KYC Checks */}
              <div className="col-md-4 mb-3">
                <div className="border p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">KYC Checks</h3>
                  <Field name="kyc_checks">
                    {({ field }) => (
                      <Checkbox.Group
                        {...field}
                        onChange={(checkedValues) => setFieldValue("kyc_checks", checkedValues)}
                        value={values.kyc_checks}
                        options={[
                          { label: "IPRS", value: "IPRS" },
                          { label: "World Check", value: "WORLDCHECK" },
                          { label: "NTSA", value: "NTSA" },
                        ]}
                      />
                    )}
                  </Field>
                  <ErrorMessage name="kyc_checks" component="div" className="text-red-500 text-sm" />
                </div>
              </div>

              {/* Submit Button */}
              <div className="col-12 mt-4">
                <Button type="primary" onClick={next} htmlType="submit"  loading={loading} block>
                  Continue
                </Button>
              </div>
            </div>
          </FormikForm>
        )}
      </Formik>
    </div>
  );
};

export default Additional;